import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { AdminService } from '../../../../core/api/admin.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CustomDateInputComponent } from '../../../../shared/components/custom-date-input/custom-date-input.component';
import { CustomButtonComponent } from '../../../../shared/components/custom-button/custom-button.component';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { DropDownSectionComponent } from '../../../../shared/components/drop-down-section/drop-down-section.component';
import { QaFieldErrorsChartComponent, FieldErrorChartItem } from '../qa-field-errors-chart/qa-field-errors-chart.component';
import { FieldErrorRateItem, SupplierErrorCount, ExtractedByType } from '../../../../core/interfaces/admin-interfaces';

interface AggregatedFieldError {
  field_name: string;
  top_supplier: string | null;
  not_extracted: number;
  wrong_value: number;
  total: number;
}

@Component({
  selector: 'app-qa-field-errors',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, CustomDateInputComponent, CustomButtonComponent, CustomDropdownComponent, DropDownSectionComponent, QaFieldErrorsChartComponent],
  templateUrl: './qa-field-errors.component.html',
  styleUrl: './qa-field-errors.component.css',
})
export class QaFieldErrorsComponent implements OnInit {
  @Input() toast!: ToastComponent;
  @Input() invoicePeriodType: string = 'mensile';

  // Date range
  fromDate: Date = new Date(new Date().setDate(new Date().getDate() - 7));
  toDate: Date = new Date();

  // Supplier filter
  supplierDropdownOptions: string[] = ['All Suppliers'];
  selectedSupplierIndex: number = 0;
  topSuppliers: SupplierErrorCount[] = [];

  // State
  loading: boolean = false;
  loaded: boolean = false;

  // Data
  chartItems: FieldErrorChartItem[] = [];
  allErrors: AggregatedFieldError[] = [];
  showDetailedBreakdown: boolean = false;

  constructor(private adminService: AdminService) { }

  ngOnInit(): void { }

  onSectionToggle(isOpen: boolean): void {
    if (isOpen && !this.loaded && !this.loading) {
      this.loadFieldErrors();
    }
  }

  onSupplierChange(): void {
    this.loadFieldErrors();
  }

  loadFieldErrors(): void {
    this.loading = true;
    this.showDetailedBreakdown = false;

    const from = this.formatDate(this.fromDate);
    const to = this.formatDate(this.toDate);
    const supplier = this.selectedSupplierIndex > 0
      ? this.topSuppliers[this.selectedSupplierIndex - 1].supplier
      : undefined;

    this.adminService.getFieldErrorRates(from, to, undefined, supplier, ExtractedByType.NEOX, this.invoicePeriodType).subscribe({
      next: (res) => {
        // Only rebuild supplier list on first load or when no supplier filter is active
        if (this.selectedSupplierIndex === 0) {
          this.topSuppliers = res.top_suppliers || [];
          this.supplierDropdownOptions = [
            'All Suppliers',
            ...this.topSuppliers.map(s => `${this.formatSupplierName(s.supplier)} (${s.count})`)
          ];
        }
        this.processData(res.data);
        this.loading = false;
        this.loaded = true;
      },
      error: () => {
        this.toast.showToast('shared.error', 'admin.qa.field_errors_load_error', 'error');
        this.loading = false;
      }
    });
  }

  private processData(items: FieldErrorRateItem[]): void {
    // Aggregate by field_name, track top supplier per field
    const map = new Map<string, AggregatedFieldError>();
    const supplierCounts = new Map<string, Map<string, number>>(); // field -> supplier -> count

    items.forEach(item => {
      const key = item.field_name;
      const existing = map.get(key) || { field_name: item.field_name, top_supplier: null, not_extracted: 0, wrong_value: 0, total: 0 };
      if (item.error_type === 'NOT_EXTRACTED') {
        existing.not_extracted += item.count;
      } else {
        existing.wrong_value += item.count;
      }
      existing.total = existing.not_extracted + existing.wrong_value;
      map.set(key, existing);

      // Track supplier counts per field
      if (item.supplier) {
        if (!supplierCounts.has(key)) supplierCounts.set(key, new Map());
        const fieldSuppliers = supplierCounts.get(key)!;
        fieldSuppliers.set(item.supplier, (fieldSuppliers.get(item.supplier) || 0) + item.count);
      }
    });

    // Set top supplier for each field
    map.forEach((err, key) => {
      const fieldSuppliers = supplierCounts.get(key);
      if (fieldSuppliers && fieldSuppliers.size > 0) {
        let topSupplier = '';
        let topCount = 0;
        fieldSuppliers.forEach((count, supplier) => {
          if (count > topCount) { topCount = count; topSupplier = supplier; }
        });
        err.top_supplier = topSupplier;
      }
    });

    // Sort by total descending
    this.allErrors = Array.from(map.values()).sort((a, b) => b.total - a.total);

    // Chart: top 10
    this.chartItems = this.allErrors.slice(0, 10).map(e => ({
      label: this.formatFieldName(e.field_name),
      not_extracted: e.not_extracted,
      wrong_value: e.wrong_value,
    }));
  }

  toggleDetailedBreakdown(): void {
    this.showDetailedBreakdown = !this.showDetailedBreakdown;
  }

  formatFieldName(name: string): string {
    return name
      .replace(/^data\./, '')
      .replace(/\./g, ' > ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  formatSupplierName(name: string): string {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  private formatDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
